import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {Avatar, IconButton, Menu, MenuItem, Tooltip, Typography} from '@mui/material'
import {useNavigate} from 'react-router-dom'
import React, {useCallback, useEffect, useState} from 'react'
import styled, {css} from 'styled-components'
import type {IUserBase} from '~/src/models'
import {useLazyGetOneAgencyQuery} from '~/src/store/apis/agency-api'
import {useSignoutMutation} from '~/src/store/apis/auth-api'
import {useAppDispatch, useAppSelector} from '~/src/store/store-hooks'
import ChangePasswordDialog from '../dialog/ChangePasswordDialog'
import EulaDialog from './EulaDialog'
import {roleApi} from '~/src/services/roleApi'

interface UserMenuProps {
  currentUser: IUserBase
}

const StyledMenuItem = styled(MenuItem)(
  ({theme}) => css`
    width: 100%;
    &:hover .itemText {
      color: ${theme.palette.primary.main};
    }
  `,
)

const StyledText = styled(Typography)(
  ({theme}) => css`
    left: 9.62%;
    right: 17.79%;
    top: 20.27%;
    bottom: 71.17%;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 19px;
    color: #383838;
  `,
)

const StyledLightText = styled(Typography)`
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 13px;
  color: #8a8a8a;
`

const UserMenu = ({currentUser}: UserMenuProps) => {
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
  const [isEulaOpen, setIsEulaOpen] = useState(false)
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [signout] = useSignoutMutation()
  const [getAgency, {error: agencyError}] = useLazyGetOneAgencyQuery()
  const [stateRole, newStateRole] = useState('')
  const {loggedInAgencyId} = useAppSelector((state) => state.root)

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUserMenu = useCallback(() => {
    setAnchorElUser(null)
  }, [setAnchorElUser])

  const handleSignOut = async () => {
    await signout({email: currentUser.emailAddress}).unwrap()
    navigate('/signin')
  }

  const handleCloseEula = useCallback(() => {
    setIsEulaOpen(false)
  }, [setIsEulaOpen])

  const handleOpenEula = useCallback(() => {
    handleCloseUserMenu()
    setIsEulaOpen(true)
  }, [handleCloseUserMenu])

  const handleCloseChangePassword = useCallback(() => {
    setIsChangePasswordOpen(false)
  }, [])

  const handleOpenChangePassword = useCallback(() => {
    handleCloseUserMenu()
    setIsChangePasswordOpen(true)
  }, [handleCloseUserMenu])

  const {emailAddress, firstName, lastName, roles} = currentUser

  useEffect(() => {
    async function fetchData() {
      const roleName = roles[0]
      
      // Handle agency admin with MSP check
      if (roleName === 'agencyadmin') {
        const agency = await getAgency(loggedInAgencyId).unwrap()
        return agency.isMsp 
          ? newStateRole('MSP User (me)') 
          : newStateRole('Agency User (me)')
      }
      
      // Handle known system roles
      const knownRoles: Record<string, string> = {
        'general_admin': 'General Admin (me)',
        'companyadmin': 'Company User (me)',
        'platformadmin': 'Platform User (me)',
        'mainagencyadmin': 'Main Agency Admin (me)',
      }
      
      if (knownRoles[roleName]) {
        return newStateRole(knownRoles[roleName])
      }
      
      // For custom roles, fetch role details to get category
      try {
        const role = await roleApi.getRoleByName(roleName)
        
        // Map category to user-friendly label
        const categoryLabels: Record<string, string> = {
          'Platform': 'Platform User',
          'MSP': 'MSP User',
          'Agency': 'Agency User',
          'Company': 'Company User',
        }
        
        const categoryLabel = categoryLabels[role.category] || 'User'
        return newStateRole(`${categoryLabel} (me)`)
      } catch {
        // Fallback if role fetch fails
        return newStateRole('User (me)')
      }
    }
    void fetchData()
  }, [getAgency, loggedInAgencyId, roles])

  return (
    <>
      <Tooltip title="Open settings">
        <IconButton onClick={handleOpenUserMenu} sx={{p: 0}}>
          <Avatar sx={{backgroundColor: '#03a9fc'}}>{firstName[0]}</Avatar>
          <ArrowDropDownIcon sx={{color: '#8A8A8A'}} />
        </IconButton>
      </Tooltip>
      <Menu
        sx={{mt: '45px'}}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}>
        <MenuItem className="itemText">
          <StyledLightText>{stateRole}</StyledLightText>
        </MenuItem>
        {/* unneeded for now */}
        {/* <StyledMenuItem>
          <StyledText textAlign="center" className="itemText">
            My Account
          </StyledText>
        </StyledMenuItem> */}
        {/* <StyledMenuItem>
          <StyledText textAlign="center" className="itemText">
            Settings
          </StyledText>
        </StyledMenuItem> */}
        <StyledMenuItem onClick={handleOpenChangePassword}>
          <StyledText textAlign="center" className="itemText">
            Change Password
          </StyledText>
        </StyledMenuItem>
        <StyledMenuItem onClick={handleOpenEula}>
          <StyledText textAlign="center" className="itemText">
            EULA
          </StyledText>
        </StyledMenuItem>
        <StyledMenuItem onClick={handleSignOut}>
          <StyledText textAlign="center" className="itemText">
            Log Out
          </StyledText>
        </StyledMenuItem>
        <MenuItem sx={{marginTop: '37px'}}>
          <Avatar
            alt="Remy Sharp"
            src="/static/images/avatar/2.jpg"
            sx={{marginRight: '13px', backgroundColor: '#03a9fc'}}>
            {firstName[0]}
          </Avatar>
          <StyledLightText>
            {emailAddress}
            <br />
            {firstName} {lastName}
          </StyledLightText>
        </MenuItem>
      </Menu>
      <EulaDialog isModalOpen={isEulaOpen} onModalClose={handleCloseEula} />
      <ChangePasswordDialog
        isOpen={isChangePasswordOpen}
        handleCancel={handleCloseChangePassword}
      />
    </>
  )
}

export default UserMenu
